/**
 * Membrane Coefficient Service
 * Provides membrane theory coefficients (Ω) for geogrid tension calculations
 *
 * Data source: ../data/membrane-coefficients.json (centralized configuration)
 * Excel source: MEMBRANE THEORY COEFFICIENT table (GEOGRID DESIGN.txt rows B51-B55)
 *
 * Used by:
 * - GeogridTtotService.ts (circular geometry calculations)
 * - GeogridTtotRectService.ts (rectangular geometry calculations)
 *
 * Purpose: Eliminates data duplication and ensures Excel-derived values remain synchronized
 */

import membraneData from '../data/membrane-coefficients.json';

interface MembraneCoefficientEntry {
    strainPercent: number;
    coefficient: number;
    excelReference?: string;
}

/**
 * Membrane coefficient lookup map
 * Loaded from centralized JSON configuration (../data/membrane-coefficients.json)
 * Values are based on Excel MEMBRANE THEORY COEFFICIENT table
 */
const MEMBRANE_COEFFICIENTS: Map<number, number> = new Map(
    membraneData.membraneCoefficients.map((entry: MembraneCoefficientEntry) => [entry.strainPercent, entry.coefficient])
);

/**
 * Default strain percentage when requested value not found
 * From Excel: typically 5% is used as fallback
 */
const DEFAULT_STRAIN = membraneData.defaultStrain;

export class MembraneCoefficientService {
    /**
     * Get membrane theory coefficient (Ω) based on design strain percentage
     *
     * Matches Excel MEMBRANE THEORY COEFFICIENT table (rows B51-B55):
     * - 1% strain → 2.07
     * - 2% strain → 1.47
     * - 3% strain → 1.23
     * - 4% strain → 1.08
     * - 5% strain → 0.97
     *
     * @param strainPercent - Design strain percentage (1-5)
     * @returns Membrane coefficient Ω
     *
     * @example
     * ```typescript
     * const omega = MembraneCoefficientService.getCoefficient(5); // Returns 0.97
     * const omegaDefault = MembraneCoefficientService.getCoefficient(7); // Returns 0.97 (fallback to 5%)
     * ```
     */
    static getCoefficient(strainPercent: number): number {
        const coefficient = MEMBRANE_COEFFICIENTS.get(strainPercent);

        if (coefficient !== undefined) {
            return coefficient;
        }

        // Fallback to default strain value (5% → 0.97)
        const defaultCoefficient = MEMBRANE_COEFFICIENTS.get(DEFAULT_STRAIN);

        if (defaultCoefficient === undefined) {
            throw new Error(
                `Membrane coefficient data corrupted: default strain ${DEFAULT_STRAIN}% not found. Check membrane-coefficients.json integrity.`
            );
        }

        return defaultCoefficient;
    }

    /**
     * Get all available strain percentages
     * Useful for validation and UI display
     *
     * @returns Array of available strain percentages (e.g., [1, 2, 3, 4, 5])
     */
    static getAvailableStrains(): number[] {
        return Array.from(MEMBRANE_COEFFICIENTS.keys()).sort((a, b) => a - b);
    }

    /**
     * Get complete coefficient mapping
     * Useful for debugging and validation
     *
     * @returns Map of strain percentage → coefficient
     */
    static getAllCoefficients(): Map<number, number> {
        return new Map(MEMBRANE_COEFFICIENTS);
    }

    /**
     * Validate if a strain percentage is explicitly defined in the table
     *
     * @param strainPercent - Strain percentage to check
     * @returns true if strain is in table, false if will use fallback
     */
    static isStrainDefined(strainPercent: number): boolean {
        return MEMBRANE_COEFFICIENTS.has(strainPercent);
    }

    /**
     * Get Excel reference for a strain percentage
     * Useful for debugging and traceability
     *
     * @param strainPercent - Strain percentage
     * @returns Excel cell reference (e.g., "B51") or undefined if not available
     */
    static getExcelReference(strainPercent: number): string | undefined {
        const entry = membraneData.membraneCoefficients.find((e: MembraneCoefficientEntry) => e.strainPercent === strainPercent);
        return entry?.excelReference;
    }
}
